// src/components/layout/Main/Sidenav.jsx
import { useState, useEffect, useCallback } from "react";
import { NavLink, useLocation, useHistory } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import {
  AppstoreOutlined,
  SearchOutlined,
  DatabaseOutlined,
  StarOutlined,
} from "@ant-design/icons";
import hooptieLogo from "../../assets/images/HooptieNewLogo.png"; // <-- your image path

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';

function Sidenav({ onMobileNavClick }) {
  const { pathname } = useLocation();
  const history = useHistory();
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const [isSubscriptionInactive, setIsSubscriptionInactive] = useState(false);
  const [watchlistOpen, setWatchlistOpen] = useState(false);
  const [watchlistItems, setWatchlistItems] = useState([]);
  const [watchlistLoading, setWatchlistLoading] = useState(false);

  // Fetch subscription status
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!isLoaded || !isSignedIn) return;
      
      try {
        const token = await getToken({ template: "default" });
        const response = await fetch(`${BASE_URL}/user/details`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (response.ok) {
          const userDetails = await response.json();
          const isInactive = userDetails.subscription_status === 'INACTIVE' || userDetails.subscription_status === 'inactive';
          setIsSubscriptionInactive(isInactive);
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
      }
    };

    checkSubscriptionStatus();
  }, [isLoaded, isSignedIn, getToken]);

  // Fetch watchlist items function
  const fetchWatchlistItems = useCallback(async () => {
    if (!isLoaded || !isSignedIn) {
      setWatchlistItems([]);
      return;
    }
    
    try {
      setWatchlistLoading(true);
      const token = await getToken({ template: "default" });
      const response = await fetch(`${BASE_URL}/watchlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Transform API response to match expected format
        // Assuming API returns array of watchlist items with vehicle info
        const items = Array.isArray(data) ? data.map((item, index) => {
          // Build vehicle name from year, make, model, trim
          const parts = [
            item.year,
            item.make,
            item.model,
            item.trim,
          ].filter(Boolean);
          const name = parts.join(' ') || 'Unknown Vehicle';
          
          return {
            id: item.id || item.vin || `watchlist-${index}`,
            name: name,
            vin: item.vin || '',
            mileage: item.mileage || null,
            version: item.version || null,
            isActive: item.is_active !== undefined ? item.is_active : true, // Default to active if not specified
          };
        }) : [];
        setWatchlistItems(items);
      } else {
        // If API returns error, set empty array
        setWatchlistItems([]);
      }
    } catch (error) {
      console.error('Error fetching watchlist:', error);
      setWatchlistItems([]);
    } finally {
      setWatchlistLoading(false);
    }
  }, [isLoaded, isSignedIn, getToken]);

  // Fetch watchlist items on mount and when auth state changes
  useEffect(() => {
    fetchWatchlistItems();
  }, [fetchWatchlistItems]);

  // Listen for watchlist update events
  useEffect(() => {
    const handleWatchlistUpdate = () => {
      fetchWatchlistItems();
    };

    window.addEventListener('watchlistUpdated', handleWatchlistUpdate);
    return () => {
      window.removeEventListener('watchlistUpdated', handleWatchlistUpdate);
    };
  }, [fetchWatchlistItems]);

  const navItems = [
    { key: "dashboard", label: "Home", icon: <AppstoreOutlined />, path: "/dashboard" },
    { key: "search-insights", label: "Market Search", icon: <SearchOutlined />, path: "/search" },
    { key: "buy-signals", label: "Buy Signals", icon: <StarOutlined />, path: "/buy-signals" },
    { key: "dealer-insights", label: "Lot Performance", icon: <DatabaseOutlined />, path: "/dealer-insights" },
  ];

  const isActive = (itemPath, itemKey) =>
    pathname === itemPath ||
    pathname.startsWith(itemPath + "/") ||
    (itemKey !== "dashboard" && itemKey !== "search-insights" && pathname.includes(itemKey));

  // Restricted items when subscription is inactive (Buy Signals, Watchlist, and others disabled)
  const restrictedItems = ['buy-signals', 'search-insights', 'recommendations', 'private-listings', 'dealer-insights'];
  const isItemDisabled = (itemKey) => isSubscriptionInactive && restrictedItems.includes(itemKey);

  const handleNavClick = (e, item) => {
    // Prevent navigation if item is disabled
    if (isItemDisabled(item.key)) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Handle external links (like mailto)
    if (item.isExternal && item.externalUrl) {
      e.preventDefault();
      window.location.href = item.externalUrl;
      if (onMobileNavClick) {
        onMobileNavClick();
      }
      return;
    }
    
    // Close mobile drawer if onMobileNavClick is provided
    if (onMobileNavClick) {
      onMobileNavClick();
    }
    
    // Force navigation for mobile
    if (window.innerWidth <= 767) {
      e.preventDefault();
      // Use history.push to force navigation
      setTimeout(() => {
        history.push(item.path);
        // Force page reload to ensure fresh content
        window.location.reload();
      }, 100);
    }
  };

  return (
    <aside className="hooptie-sidenav">
      {/* neon rail */}
      <div className="hooptie-rail" />

      {/* logo only - hidden on mobile */}
      <div className="hooptie-brand mobile-hidden">
        <img
          src={hooptieLogo}
          alt="Hooptie"
          className="hooptie-logo"
        />
      </div>

      {/* Desktop navigation - always visible */}
      <nav className="hooptie-nav">
        {navItems.map((item) => {
          if (item.isExternal) {
            return (
              <div
                key={item.key}
                className="nav-link"
                onClick={(e) => handleNavClick(e, item)}
                style={{ cursor: 'pointer' }}
              >
                <div className={`nav-item ${isActive(item.path, item.key) ? "active" : ""}`}>
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </div>
              </div>
            );
          }
          const isDisabled = isItemDisabled(item.key);
          return (
            <NavLink 
              key={item.key} 
              to={isDisabled ? "#" : item.path} 
              className={`nav-link ${isDisabled ? 'nav-link-disabled' : ''}`}
              onClick={(e) => handleNavClick(e, item)}
              style={{
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.5 : 1,
                pointerEvents: isDisabled ? 'none' : 'auto'
              }}
            >
              <div className={`nav-item ${isActive(item.path, item.key) ? "active" : ""}`}>
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </div>
            </NavLink>
          );
        })}
        {/* Watchlist dropdown - disabled when subscription inactive */}
        <div className={`sidenav-watchlist ${isSubscriptionInactive ? 'sidenav-watchlist-disabled' : ''}`}>
          <div
            className="nav-link"
            onClick={() => !isSubscriptionInactive && setWatchlistOpen(!watchlistOpen)}
            role="button"
            tabIndex={isSubscriptionInactive ? -1 : 0}
            onKeyDown={(e) => !isSubscriptionInactive && e.key === 'Enter' && setWatchlistOpen(!watchlistOpen)}
            style={{
              cursor: isSubscriptionInactive ? 'not-allowed' : 'pointer',
              opacity: isSubscriptionInactive ? 0.5 : 1,
              pointerEvents: isSubscriptionInactive ? 'none' : 'auto',
            }}
          >
            <div className={`nav-item ${watchlistOpen ? 'active' : ''}`}>
              <span className="nav-icon"><StarOutlined /></span>
              <span className="nav-label">Watchlist</span>
            </div>
          </div>
          {watchlistOpen && (
            <div className="sidenav-watchlist-list">
              {watchlistLoading ? (
                <div className="sidenav-watchlist-empty">Loading...</div>
              ) : watchlistItems.length === 0 ? (
                <div className="sidenav-watchlist-empty">No vehicles yet</div>
              ) : (
                watchlistItems.map((item) => {
                  const handleWatchlistItemClick = () => {
                    if (!item.vin) return;
                    
                    // Build query parameters
                    const params = new URLSearchParams();
                    params.set('vin', item.vin);
                    if (item.mileage) params.set('mileage', item.mileage.toString());
                    if (item.version) params.set('version', item.version);
                    
                    // Navigate to search page with query params
                    const searchUrl = `/search?${params.toString()}`;
                    history.push(searchUrl);
                    
                    // Close watchlist dropdown
                    setWatchlistOpen(false);
                  };
                  
                  return (
                    <div 
                      key={item.id} 
                      className="sidenav-watchlist-item"
                      onClick={handleWatchlistItemClick}
                      style={{ cursor: 'pointer' }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleWatchlistItemClick();
                        }
                      }}
                    >
                      <span className={`sidenav-watchlist-bullet ${item.isActive ? 'active' : ''}`} />
                      <div className="sidenav-watchlist-item-content">
                        <div className="sidenav-watchlist-item-name">{item.name}</div>
                        <div className="sidenav-watchlist-item-vin">{item.vin}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Mobile menu panel - only visible on mobile */}
      <div className="mobile-menu-panel">
        {/* Mobile navigation header */}
        <div className="mobile-nav-header">
          <div className="mobile-nav-brand">
            <h2 className="mobile-nav-title">Hooptie</h2>
            <div className="mobile-nav-close" onClick={onMobileNavClick}>
              <span>×</span>
            </div>
          </div>
        </div>

        {/* Mobile nav - same items but in mobile panel */}
        <nav className="hooptie-nav mobile-nav">
          {navItems.map((item) => {
            if (item.isExternal) {
              return (
                <div
                  key={item.key}
                  className="nav-link"
                  onClick={(e) => handleNavClick(e, item)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className={`nav-item ${isActive(item.path, item.key) ? "active" : ""}`}>
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-label">{item.label}</span>
                  </div>
                </div>
              );
            }
            const isDisabled = isItemDisabled(item.key);
            return (
              <NavLink 
                key={item.key} 
                to={isDisabled ? "#" : item.path} 
                className={`nav-link ${isDisabled ? 'nav-link-disabled' : ''}`}
                onClick={(e) => handleNavClick(e, item)}
                style={{
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.5 : 1,
                  pointerEvents: isDisabled ? 'none' : 'auto'
                }}
              >
                <div className={`nav-item ${isActive(item.path, item.key) ? "active" : ""}`}>
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </div>
              </NavLink>
            );
          })}
          {/* Watchlist dropdown - mobile, disabled when subscription inactive */}
          <div className={`sidenav-watchlist ${isSubscriptionInactive ? 'sidenav-watchlist-disabled' : ''}`}>
            <div
              className="nav-link"
              onClick={() => !isSubscriptionInactive && setWatchlistOpen(!watchlistOpen)}
              role="button"
              tabIndex={isSubscriptionInactive ? -1 : 0}
              onKeyDown={(e) => !isSubscriptionInactive && e.key === 'Enter' && setWatchlistOpen(!watchlistOpen)}
              style={{
                cursor: isSubscriptionInactive ? 'not-allowed' : 'pointer',
                opacity: isSubscriptionInactive ? 0.5 : 1,
                pointerEvents: isSubscriptionInactive ? 'none' : 'auto',
              }}
            >
              <div className={`nav-item ${watchlistOpen ? 'active' : ''}`}>
                <span className="nav-icon"><StarOutlined /></span>
                <span className="nav-label">Watchlist</span>
              </div>
            </div>
            {watchlistOpen && (
              <div className="sidenav-watchlist-list">
                {watchlistLoading ? (
                  <div className="sidenav-watchlist-empty">Loading...</div>
                ) : watchlistItems.length === 0 ? (
                  <div className="sidenav-watchlist-empty">No vehicles yet</div>
                ) : (
                  watchlistItems.map((item) => {
                    const handleWatchlistItemClick = () => {
                      if (!item.vin) return;
                      
                      // Build query parameters
                      const params = new URLSearchParams();
                      params.set('vin', item.vin);
                      if (item.mileage) params.set('mileage', item.mileage.toString());
                      if (item.version) params.set('version', item.version);
                      
                      // Navigate to search page with query params
                      const searchUrl = `/search?${params.toString()}`;
                      history.push(searchUrl);
                      
                      // Close mobile menu if open
                      if (onMobileNavClick) {
                        onMobileNavClick();
                      }
                      
                      // Close watchlist dropdown
                      setWatchlistOpen(false);
                    };
                    
                    return (
                      <div 
                        key={item.id} 
                        className="sidenav-watchlist-item"
                        onClick={handleWatchlistItemClick}
                        style={{ cursor: 'pointer' }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleWatchlistItemClick();
                          }
                        }}
                      >
                        <span className={`sidenav-watchlist-bullet ${item.isActive ? 'active' : ''}`} />
                        <div className="sidenav-watchlist-item-content">
                          <div className="sidenav-watchlist-item-name">{item.name}</div>
                          <div className="sidenav-watchlist-item-vin">{item.vin}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </nav>

      </div>

    </aside>
  );
}

export default Sidenav;
